/**
 * ChangeSummary Component
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.8 - Create change summary display
 *
 * Displays what was modified in the generated resume.
 */

import React from 'react';

const ChangeSummary = ({ changes, generatedAt, jobTitle }) => {
  if (!changes) return null;

  // Handle both integer and array formats for sections_modified
  const rawSectionsModified = changes.sections_modified;
  const sectionsModifiedCount = typeof rawSectionsModified === 'number'
    ? rawSectionsModified
    : (Array.isArray(rawSectionsModified) ? rawSectionsModified.length : 0);
  const sectionsModifiedList = Array.isArray(rawSectionsModified) ? rawSectionsModified : [];

  const sectionsUnchanged = changes.sections_unchanged || 0;
  const summary = changes.summary || '';
  const keywords_added = changes.keywords_added || [];
  const customizations = changes.customizations || [];

  // Format the timestamp
  const formatDate = (isoString) => {
    if (!isoString) return 'Just now';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  return (
    <div className="bg-slate-700 border border-slate-600 rounded-lg p-6 mt-6">
      {/* Header */}
      <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
        <svg className="w-5 h-5 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Change Summary
      </h3>

      {/* Metadata */}
      <div className="space-y-2 mb-4 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-400">Generated:</span>
          <span className="text-slate-200">{formatDate(generatedAt)}</span>
        </div>
        {jobTitle && (
          <div className="flex justify-between">
            <span className="text-slate-400">Job Title:</span>
            <span className="text-slate-200">{jobTitle}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <hr className="border-slate-600 my-4" />

      {/* Summary text */}
      {summary && (
        <div className="mb-4">
          <p className="text-sm text-slate-300">{summary}</p>
        </div>
      )}

      {/* Sections Modified */}
      {sectionsModifiedCount > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-slate-300 mb-2">
            Sections Modified: {sectionsModifiedCount} of {sectionsModifiedCount + sectionsUnchanged}
          </h4>
          {sectionsModifiedList.length > 0 && (
            <ul className="space-y-1">
              {sectionsModifiedList.map((section, index) => (
                <li key={index} className="text-sm text-slate-400 flex items-center gap-2">
                  <span className="text-sky-400">-</span>
                  {section}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Keywords Added */}
      {keywords_added.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-slate-300 mb-2">
            Keywords Added:
          </h4>
          <p className="text-sm text-slate-400">
            {keywords_added.join(', ')}
          </p>
        </div>
      )}

      {/* Customizations */}
      {customizations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-slate-300 mb-2">
            Customizations:
          </h4>
          <ul className="space-y-1">
            {customizations.map((custom, index) => (
              <li key={index} className="text-sm text-slate-400 flex items-center gap-2">
                <span className="text-green-400">+</span>
                {custom}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Empty state */}
      {sectionsModifiedCount === 0 && keywords_added.length === 0 && customizations.length === 0 && !summary && (
        <p className="text-sm text-slate-500 italic">
          No detailed changes available.
        </p>
      )}
    </div>
  );
};

export default ChangeSummary;
